import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Mail, Lock, Sprout } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || "/";

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [demo, setDemo] = useState(null);

    useEffect(() => {
        axios.get(`${API}/demo/credentials`).then((r) => setDemo(r.data)).catch(() => {});
    }, []);

    if (user && user !== false) return <Navigate to={from} replace />;

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            await login(email, password);
            navigate(from, { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const tryDemo = async () => {
        if (!demo?.available) return;
        setError("");
        setBusy(true);
        try {
            await login(demo.email, demo.password);
            navigate("/", { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen paper-texture flex items-center justify-center p-6" data-testid="login-page">
            <div className="w-full max-w-md">
                <div className="flex items-center gap-3 mb-8 justify-center">
                    <Logo variant="icon" size={56} />
                    <div>
                        <div className="font-serif-display text-3xl text-[var(--vm-text)] leading-none lowercase tracking-tight">rooteed</div>
                        <div className="text-xs uppercase tracking-[0.2em] text-[var(--vm-sage)] mt-1">Garden Journal</div>
                    </div>
                </div>

                <div className="journal-card p-8">
                    <h1 className="font-serif-display text-3xl text-[var(--vm-text)] mb-2">Welcome back</h1>
                    <p className="text-sm text-[var(--vm-text-muted)] mb-6">
                        Sign in to tend to your garden, log harvests, and chat with your mentor.
                    </p>

                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">Email</Label>
                            <div className="relative mt-1">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--vm-text-muted)]" />
                                <Input
                                    id="email" type="email" required autoFocus autoComplete="email"
                                    data-testid="login-email-input"
                                    value={email} onChange={(e) => setEmail(e.target.value)}
                                    className="pl-9 bg-[var(--vm-bg)] border-[var(--vm-border)]"
                                    placeholder="you@garden.com"
                                />
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                                <Label htmlFor="password" className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">Password</Label>
                                <Link
                                    to="/forgot-password"
                                    data-testid="login-forgot-link"
                                    className="text-xs text-[var(--vm-text-muted)] hover:text-[var(--vm-text)] underline"
                                >
                                    Forgot?
                                </Link>
                            </div>
                            <div className="relative mt-1">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--vm-text-muted)]" />
                                <Input
                                    id="password" type="password" required autoComplete="current-password"
                                    data-testid="login-password-input"
                                    value={password} onChange={(e) => setPassword(e.target.value)}
                                    className="pl-9 bg-[var(--vm-bg)] border-[var(--vm-border)]"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="text-sm text-[var(--vm-secondary)] bg-[var(--vm-secondary)]/10 rounded px-3 py-2" data-testid="login-error">
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit" disabled={busy}
                            data-testid="login-submit-button"
                            className="w-full rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)] py-5"
                        >
                            {busy ? "Signing in…" : "Sign in"}
                        </Button>
                    </form>

                    <p className="mt-6 text-sm text-[var(--vm-text-muted)] text-center">
                        New to rooteed?{" "}
                        <Link to="/register" data-testid="register-link" className="text-[var(--vm-accent)] underline">
                            Plant your first seed
                        </Link>
                        <span className="mx-1.5">·</span>
                        <Link to="/about" data-testid="about-link" className="text-[var(--vm-text-secondary)] hover:text-[var(--vm-text)] underline">
                            Learn more
                        </Link>
                    </p>

                    {demo?.available && (
                        <div
                            className="mt-6 pt-6 border-t border-[var(--vm-border)]"
                            data-testid="demo-try-card"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <Sprout className="w-4 h-4 text-[var(--vm-sage)]" />
                                <span className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">
                                    Just browsing?
                                </span>
                            </div>
                            <p className="text-sm text-[var(--vm-text-secondary)] mb-3">
                                Jump into a ready-made {demo.garden_size_m.length}×{demo.garden_size_m.width} m
                                demo allotment with {demo.beds} beds, {demo.crops} crops and a full season of notes.
                            </p>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={tryDemo}
                                disabled={busy}
                                data-testid="demo-try-button"
                                className="w-full rounded-full border-[var(--vm-sage)] text-[var(--vm-sage)]"
                            >
                                <Sprout className="w-4 h-4 mr-2" />
                                Try the demo garden
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
